import {
    FetchState,
    ReportCategory,
    QuickSightReportState,
    QuickSightReport
} from "@/shared/types";
import * as UserService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";

export const initialState: QuickSightReportState = {
    fetchState: FetchState.Uninitialized,
    postFetchState: FetchState.Uninitialized,
    categoryFetchState: FetchState.Uninitialized,
    categories: [],
    reports: [],
};

export const useQuickSightReportStore = defineStore("quickSightReport", {
    state: (): QuickSightReportState => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: state => state.fetchState === FetchState.Loaded,
        isPosting: state => state.postFetchState === FetchState.Loading,
        reportCategories: state => state.categories,
        isCategoryLoaded: state => state.categoryFetchState === FetchState.Loaded,
        reportById: state => (reportId: number) => {
            for (const report of state.reports) {
                if (report.quickSightReportId === reportId) {
                    return report;
                }
            }

            return null;
        },
    },
    actions: {
        setReports (reports: QuickSightReport[]) {
            this.reports = reports || [];
        },
        setGetRequestFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setPostRequestFetchState (fetchState: FetchState) {
            this.postFetchState = fetchState;
        },
        setCategoryFetchState (fetchState: FetchState) {
            this.categoryFetchState = fetchState;
        },
        setCategories (reportCategories: ReportCategory[]) {
            this.categories = [ ...reportCategories ];
        },
        getReports (): Promise<void> {
            if (this.postFetchState === FetchState.Loading) {
                return;
            }

            this.setGetRequestFetchState( FetchState.Loading);

            return UserService.getQuickSightReport().then(resp => {
                this.setGetRequestFetchState( FetchState.Loaded);
                this.setReports( resp.data);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        deleteReport (reportId: number): Promise<void> {
            const notifierStore = useNotifierStore();
            return new Promise(resolve => {
                UserService.deleteQuickSightReport(reportId).then(() => {
                    notifierStore.setSuccess( `The report has been deleted`);
                    resolve();
                }).catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
            });
        },
        saveQuickSightReport (quickSightReport: QuickSightReport): Promise<void> {
            if (this.postFetchState === FetchState.Loading) {
                return;
            }

            this.setPostRequestFetchState(FetchState.Loading);

            return UserService.saveQuickSightReport(quickSightReport).then(resp => {
                this.setPostRequestFetchState( FetchState.Loaded);
                const notifierStore = useNotifierStore();
                notifierStore.setSuccess( `The report has been saved`);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        getCategories () {
            return new Promise<void>((resolve, reject) => {
                if (this.categoryFetchState === FetchState.Loading) {
                    resolve();
                    return;
                } else {
                    this.setCategoryFetchState( FetchState.Loading);
                    return UserService.getReportCategories().then(resp => {
                        this.setCategories( resp.data);
                        this.setCategoryFetchState( FetchState.Loaded);
                    }).catch(err => {
                        storeHelper.handleError({ error: err, rethrow: true });
                    });
                }
            });
        },
    }
});